/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-len */
import useFlags from 'launchdarkly-react-client-sdk/lib/useFlags';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { BiogenFunction, Channel, VaultDocumentStatuses, VeevaVaultBiogenFunction } from 'basics/enums/maya.enums';
import useGetAllVeevaVaultDocument, { useGetAllVeevaVaultDocumentLazy } from 'basics/graphql/queries/getAllVeevaVaultDocument';
import useApprovedDocumentIdOptions from 'basics/options/approvedDocumentId.options';
import { CepStateType, TacticStateType } from 'basics/types/maya.types';
import { warningToast } from 'basics/utils/toast';
import { ProductsContext } from 'contexts/products';
import {
  CustomerInitiative,
  VeevaVaultDocument,
  VeevaVaultDocumentFilter,
  VeevaVaultDocumentLimit,
} from 'generated/maya.types';
import cepState from 'states/cep.states';
import tacticState from 'states/tactic.states';

type FetchTacticApprovedDocumentsOptionsParams = {
  country?: string;
  product?: string;
  executingTeam?: string;
  currentDocumentId?: string;
  includeNotCertifiedDocuments?: boolean;
};

type DocumentOption = {
  id: string;
  label: string;
};

const isApprovedDocument = (document: VeevaVaultDocument | undefined) => {
  if (!document) return false;

  return document.status === VaultDocumentStatuses.Approved
      || document.status === VaultDocumentStatuses.ApprovedForProduction
      || document.status === VaultDocumentStatuses.ApprovedWithChanges;
};

const isDocumentAvailableForExecutingTeam = (
  document: VeevaVaultDocument,
  executingTeam?: string,
) => {
  switch (executingTeam) {
    case BiogenFunction.Commercial:
      return Boolean(document.executingTeam?.includes(VeevaVaultBiogenFunction.Commercial));
    case BiogenFunction.Medical:
      return Boolean(document.executingTeam?.includes(VeevaVaultBiogenFunction.Medical));
    case BiogenFunction.MarketAccess:
      return Boolean(document.executingTeam?.includes(VeevaVaultBiogenFunction.MarketAccess));
    default:
      return false;
  }
};

const mapDocumentsToOptions = (documents: VeevaVaultDocument[]): DocumentOption[] => {
  return documents.map((document) => ({
    id: document.id.toString(),
    label: `[${document.documentNumber}] [${document.status}] ${document.name}`,
  }));
};

export const useFetchTacticApprovedDocumentsOptions = () => {
  const flags = useFlags();
  const { getAllVeevaVaultDocumentLazy } = useGetAllVeevaVaultDocumentLazy();

  const fetchTacticApprovedDocumentsOptions = async ({
    country,
    product,
    executingTeam,
    currentDocumentId,
    includeNotCertifiedDocuments = false,
  }: FetchTacticApprovedDocumentsOptionsParams): Promise<DocumentOption[]> => {
    if (!country || !product) {
      return [];
    }

    const { data } = await getAllVeevaVaultDocumentLazy({
      variables: {
        limit: {
          limit: 5000,
        },
        filters: {
          country,
          product,
          currentDocumentId,
        },
      },
    });

    const filteredDocuments = (data?.getAllVeevaVaultDocument || [])
      .filter((document: VeevaVaultDocument) => isDocumentAvailableForExecutingTeam(document, executingTeam))
      .filter((document: VeevaVaultDocument) => {
        if (includeNotCertifiedDocuments || flags.mayaAkma759ShowNotCertifiedDocuments) {
          return true;
        }

        return isApprovedDocument(document);
      });

    return mapDocumentsToOptions(filteredDocuments);
  };

  return {
    fetchTacticApprovedDocumentsOptions,
  };
};

const useApprovedDocuments = (
  executingTeam: BiogenFunction,
  channel: Channel,
  selectedApproveDocumentId: string,
  setValue: (fieldName: string, fieldValue: any) => void,
) => {
  const { t } = useTranslation();
  const flags = useFlags();
  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const { productList } = useContext(ProductsContext);
  const currentProduct = productList?.find((product) => product.productExternalID === cepStateValue?.cep?.product);
  const customerInitiative = cepStateValue.cep?.customerInitiative as CustomerInitiative;
  const { country } = customerInitiative;
  const [documents, setDocuments] = useState<VeevaVaultDocument[]>([]);
  const [loadingStarted, setLoadingStarted] = useState(false);
  const tacticStateValue = useSnapshot(tacticState) as TacticStateType;
  const currentDocumentId = tacticStateValue.tactic?.approvedDocumentId;
  const [showNotCertifiedDocuments, setShowNotCertifiedDocuments] = useState(false);
  const isCurrentExecutingTeam = executingTeam && executingTeam === tacticStateValue?.tactic?.executingTeam;

  const vaultDocumentFilter: VeevaVaultDocumentFilter = {
    country,
    product: currentProduct?.productID,
    currentDocumentId: tacticStateValue.tactic?.approvedDocumentId,
  };

  const vaultDocumentLimit: VeevaVaultDocumentLimit = {
    limit: 5000,
  };

  const {
    data: documentsData,
    loading: allVeevaVaultDocumentsLoading,
  } = useGetAllVeevaVaultDocument(vaultDocumentLimit, vaultDocumentFilter);

  const allApprovedDocumentIdOptions = useApprovedDocumentIdOptions(
    documents,
    currentDocumentId,
    !allVeevaVaultDocumentsLoading,
    Boolean(isCurrentExecutingTeam),
  );

  useEffect(() => {
    const filteredDocumentsByExecutingTeam = documentsData.filter((document) => {
      return isDocumentAvailableForExecutingTeam(document, executingTeam);
    });

    let filteredDocuments;

    if (flags.mayaAkma759ShowNotCertifiedDocuments) {
      filteredDocuments = filteredDocumentsByExecutingTeam.filter((document) => {
        if (!showNotCertifiedDocuments && isApprovedDocument(document)) return document;
        if (showNotCertifiedDocuments) return document;

        return false;
      });
    } else {
      filteredDocuments = filteredDocumentsByExecutingTeam;
    }

    setDocuments(filteredDocuments);

    if (allVeevaVaultDocumentsLoading) {
      setLoadingStarted(true);
    }

    const isLoadedDocuments = loadingStarted && !allVeevaVaultDocumentsLoading;
    const isValidChannel = Channel.approvedEmail === channel;

    if (isLoadedDocuments && !filteredDocumentsByExecutingTeam.length && executingTeam && isValidChannel) {
      warningToast(t('maya_approved_documents_warning'));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executingTeam, showNotCertifiedDocuments, allVeevaVaultDocumentsLoading]);

  useEffect(() => {
    const currentDocument = documentsData?.find((document) => document.id.toString() === currentDocumentId);

    if (currentDocument && !isApprovedDocument(currentDocument)) {
      setShowNotCertifiedDocuments(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allVeevaVaultDocumentsLoading]);

  const handleVAEFilterChanging = (value: boolean) => {
    setShowNotCertifiedDocuments(value);
  };

  useEffect(() => {
    const notEqualDocuments = selectedApproveDocumentId && selectedApproveDocumentId !== currentDocumentId;
    const currentDocument = documentsData?.find((document) => document?.id.toString() === selectedApproveDocumentId);

    if (notEqualDocuments && !isApprovedDocument(currentDocument)) {
      warningToast(t('maya_not_approved_documents_warning'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApproveDocumentId]);

  useEffect(() => {
    const isLoadedDocuments = loadingStarted && !allVeevaVaultDocumentsLoading;

    if (!isLoadedDocuments || !selectedApproveDocumentId || !executingTeam) {
      return;
    }

    const selectedDocument = documentsData?.find((document) => {
      return document?.id.toString() === selectedApproveDocumentId.toString();
    });

    if (selectedDocument && !isDocumentAvailableForExecutingTeam(selectedDocument, executingTeam)) {
      setValue('approvedDocumentId', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    executingTeam,
    selectedApproveDocumentId,
    documentsData,
    loadingStarted,
    allVeevaVaultDocumentsLoading,
  ]);

  return {
    allApprovedDocumentIdOptions,
    allVeevaVaultDocumentsLoading,
    showNotCertifiedDocuments,
    handleVAEFilterChanging,
  };
};

export default useApprovedDocuments;
